import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CircularProgress from '@mui/material/CircularProgress'
import ActionsAPI from 'services/ActionsAPI'
import { getActionStatsRoutine } from 'features/actions/ducks/actions'

const SEVERITY_TINT = {
  critical: { bg: '#fdecea', fg: '#b00020' },
  high:     { bg: '#ffedd5', fg: '#c2410c' },
  medium:   { bg: '#fef3c7', fg: '#d97706' },
  low:      { bg: '#dcfce7', fg: '#15803d' },
}

/**
 * Review panel rendered on a signed-off task. Lists Actions Guru extracted
 * from the task's completed form (status = pending_review). Each row has
 * Approve (→ open queue) and Dismiss buttons; processed rows fade out and
 * the panel auto-hides when the queue empties.
 *
 * The signoff backend dispatches a queued job (ExtractActionsFromTaskForm)
 * so when this panel first mounts after signoff the list may briefly be
 * empty — we poll for ~30s before settling on "no remedials extracted".
 */
const TaskPendingActionsPanel = ({ taskId }) => {
  const dispatch = useDispatch()
  const [actions, setActions] = useState(null) // null = first load
  const [loading, setLoading] = useState(true)
  // After the polling window closes with nothing, we show a "no follow-ups
  // found" state instead of hiding the panel entirely — otherwise the user
  // can't tell whether extraction ran.
  const [pollingDone, setPollingDone] = useState(false)
  const [busyIds, setBusyIds] = useState(new Set())
  const [reason, setReason] = useState({}) // per-action dismiss reason

  const refresh = useCallback(async () => {
    try {
      const res = await ActionsAPI.list({
        'filter[source_task_id]': taskId,
        'filter[status]': 'pending_review',
        per_page: 50,
      })
      const items = res?.data?.data || []
      setActions(items)
      return items
    } catch (e) {
      setActions([])
      return []
    } finally {
      setLoading(false)
    }
  }, [taskId])

  useEffect(() => {
    if (!taskId) return
    let cancelled = false
    let pollHandle = null

    // Reset state when the task id changes — without this, a previous task's
    // "polling done" verdict would carry over and the loading state would
    // skip on the new task.
    setActions(null)
    setLoading(true)
    setPollingDone(false)

    const initial = async () => {
      const items = await refresh()
      if (cancelled) return
      if (items.length > 0) {
        setPollingDone(true)
        return
      }
      // Empty after the first fetch — the signoff job may still be queued.
      // Poll briefly so the panel populates without a manual refresh.
      const start = Date.now()
      const tick = async () => {
        if (cancelled) return
        if (Date.now() - start > 30_000) {
          if (!cancelled) setPollingDone(true)
          return
        }
        const next = await refresh()
        if (cancelled) return
        if (next.length > 0) {
          setPollingDone(true)
          return
        }
        pollHandle = setTimeout(tick, 4000)
      }
      pollHandle = setTimeout(tick, 4000)
    }
    initial()

    return () => {
      cancelled = true
      if (pollHandle) clearTimeout(pollHandle)
    }
  }, [taskId, refresh])

  const setBusy = (id, on) => {
    setBusyIds((prev) => {
      const next = new Set(prev)
      if (on) next.add(id); else next.delete(id)
      return next
    })
  }

  const onApprove = async (action) => {
    setBusy(action.id, true)
    try {
      await ActionsAPI.approve(action.id)
      setActions((prev) => (prev || []).filter((a) => a.id !== action.id))
      // Approval moves the row from pending_review → open, which bumps
      // the actions hub badge and the open count on the Actions card.
      dispatch(getActionStatsRoutine({}))
      toast.success('Remedial approved. Added to the open queue.')
    } catch (e) {
      toast.error('Could not approve this remedial. Please try again.')
    } finally {
      setBusy(action.id, false)
    }
  }

  const onDismiss = async (action) => {
    setBusy(action.id, true)
    try {
      await ActionsAPI.dismiss(action.id, { reason: reason[action.id] || null })
      setActions((prev) => (prev || []).filter((a) => a.id !== action.id))
      dispatch(getActionStatsRoutine({}))
      toast.info('Removed as not a real issue.')
    } catch (e) {
      toast.error('This AI find could not be removed. Please try again.')
    } finally {
      setBusy(action.id, false)
    }
  }

  // Loading state — the very first fetch hasn't returned yet.
  if (loading && actions === null) {
    return (
      <Panel>
        <PanelHead>
          <PanelTitle>
            <BoltOutlinedIcon style={{ fontSize: 18, color: '#D24724' }} />
            Reviewing this report for follow-ups…
          </PanelTitle>
        </PanelHead>
        <Loading><CircularProgress size={18} sx={{ color: '#D24724' }} /></Loading>
      </Panel>
    )
  }

  // Still empty — and we're either still polling or polling completed
  // without finding anything. Show the state explicitly so the user knows
  // extraction ran rather than wondering why nothing surfaced.
  if (!actions || actions.length === 0) {
    return (
      <Panel>
        <PanelHead>
          <PanelTitle>
            <BoltOutlinedIcon style={{ fontSize: 18, color: '#D24724' }} />
            {pollingDone ? 'No follow-up remedials found' : 'Reviewing this report for follow-ups…'}
          </PanelTitle>
          <PanelHint>
            {pollingDone
              ? "Guru read the completed form and didn't find anything actionable. If you spot something the system missed, you can add a remedial manually from the Remedials tab."
              : 'Guru is reading the completed form for issues that may need follow-up. This usually finishes in a few seconds.'}
          </PanelHint>
        </PanelHead>
        {!pollingDone && <Loading><CircularProgress size={18} sx={{ color: '#D24724' }} /></Loading>}
      </Panel>
    )
  }

  return (
    <Panel>
      <PanelHead>
        <PanelTitle>
          <BoltOutlinedIcon style={{ fontSize: 18, color: '#D24724' }} />
          {actions.length} suggested remedial{actions.length === 1 ? '' : 's'} from this report
        </PanelTitle>
        <PanelHint>
          Guru read the completed form and flagged follow-up items. Approve the real ones to
          add them to your open remedials. If one is not a real problem, choose
          &ldquo;Not a real issue&rdquo; to remove it.
        </PanelHint>
      </PanelHead>

      <List>
        {actions.map((a) => {
          const tint = SEVERITY_TINT[a.severity] || { bg: '#f1f5f9', fg: '#475569' }
          const isBusy = busyIds.has(a.id)
          return (
            <Row key={a.id}>
              <RowMain>
                <RowTitle>{a.title}</RowTitle>
                {a.description && <RowDesc>{a.description}</RowDesc>}
                <RowMeta>
                  <SeverityChip $bg={tint.bg} $fg={tint.fg}>
                    {a.severity}
                  </SeverityChip>
                  {a.suggestedDueBy && (
                    <DueChip>
                      <HourglassEmptyIcon style={{ fontSize: 12 }} />
                      Due {a.suggestedDueBy}
                    </DueChip>
                  )}
                </RowMeta>
              </RowMain>

              <RowActions>
                <DismissReasonInput
                  type="text"
                  placeholder="Why? (optional)"
                  value={reason[a.id] || ''}
                  onChange={(e) => setReason((r) => ({ ...r, [a.id]: e.target.value }))}
                  disabled={isBusy}
                />
                <DismissBtn
                  type="button"
                  onClick={() => onDismiss(a)}
                  disabled={isBusy}
                  title="Remove this AI find because it is not a real problem"
                >
                  <CloseIcon style={{ fontSize: 16 }} /> Not a real issue
                </DismissBtn>
                <ApproveBtn
                  type="button"
                  onClick={() => onApprove(a)}
                  disabled={isBusy}
                  title="Approve and send to the open queue"
                >
                  <CheckIcon style={{ fontSize: 16 }} /> Approve
                </ApproveBtn>
              </RowActions>
            </Row>
          )
        })}
      </List>
    </Panel>
  )
}

export default TaskPendingActionsPanel

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`

const Panel = styled.div`
  background: white;
  border: 1px solid #fbcdb5;
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  animation: ${fadeIn} 0.25s ease-out;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const PanelHead = styled.div`
  margin-bottom: 12px;
`

const PanelTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`

const PanelHint = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
  max-width: 720px;
  line-height: 1.5;
`

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Row = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafbfc;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const RowMain = styled.div`
  flex: 1;
  min-width: 0;
`

const RowTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 4px;
`

const RowDesc = styled.div`
  font-size: 12px;
  color: #475569;
  line-height: 1.5;
  margin-bottom: 8px;
`

const RowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

const SeverityChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
`

const DueChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #475569;
`

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    width: 100%;
  }
`

const DismissReasonInput = styled.input`
  font-size: 12px;
  padding: 7px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  width: 160px;
  &:focus { outline: none; border-color: #94a3b8; }

  @media (max-width: 768px) {
    flex: 1 1 100%;
    width: auto;
  }
`

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 40px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  &:disabled { opacity: 0.5; cursor: progress; }
`

const DismissBtn = styled(Btn)`
  background: transparent;
  border: 1px solid #e5e7eb;
  color: #475569;
  &:hover:not(:disabled) { background: #f8fafc; color: #0f172a; }
`

const ApproveBtn = styled(Btn)`
  background: #D24724;
  border: 1px solid #D24724;
  color: white;
  &:hover:not(:disabled) { background: #b53a1d; border-color: #b53a1d; }
`
